import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import {
  formatCurrency,
  formatDate,
  parseCurrencyAmount,
} from "../../utils/utils";
import { submitDispute, appealDispute } from "../../api/disputes";
import {
  DISPUTE_REASONS,
  ATTACHMENT_REQUIRED_REASONS,
  SUPPORTED_FILE_ACCEPT,
  SUPPORTED_FILE_EXTENSIONS,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
} from "../../constants/disputes";
import "./DisputeFormModal.css";

const isSupportedFile = (file) => {
  const name = file.name.toLowerCase();
  return SUPPORTED_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const ReadOnlyField = ({ label, value }) => (
  <div className="dispute-form-field">
    <span className="dispute-form-field-label">{label}</span>
    <span className="dispute-form-field-value">{value || "-"}</span>
  </div>
);

ReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const AmountTile = ({ label, value, currency, highlight }) => (
  <div
    className={`dispute-amount-tile ${
      highlight ? "dispute-amount-tile--highlight" : ""
    }`}
  >
    <span className="dispute-amount-tile-label">{label}</span>
    <span className="dispute-amount-tile-value">
      {formatCurrency(value || 0, currency || "USD")}
    </span>
  </div>
);

AmountTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.string,
  highlight: PropTypes.bool,
};

const DisputeFormModal = ({
  isOpen,
  mode,
  htConfirmationId,
  bookingDetails,
  invoiceAmount,
  transactedPlatformFee,
  adjustedPlatformFee,
  currency,
  currentTransactedBookingAmount,
  defaultAgreedAmount,
  disputeId,
  onClose,
  onSubmitted,
}) => {
  const isAppeal = mode === "appeal";

  const [agreedAmount, setAgreedAmount] = useState("");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Reset all form state whenever the modal (re)opens. On appeal the Client
  // Agreed Booking Amount is pre-filled from the existing dispute (editable).
  useEffect(() => {
    if (!isOpen) return;
    setAgreedAmount(
      defaultAgreedAmount !== undefined && defaultAgreedAmount !== null
        ? String(defaultAgreedAmount)
        : ""
    );
    setReason("");
    setDescription("");
    setFiles([]);
  }, [isOpen, defaultAgreedAmount]);

  const numericAgreed = parseCurrencyAmount(agreedAmount);
  const hasAgreed = agreedAmount !== "" && !isNaN(numericAgreed);
  const attachmentRequired = ATTACHMENT_REQUIRED_REASONS.includes(reason);

  // The transacted booking amount comes from the booking payload on submit
  // (invoiceAmount) or from the dispute detail on appeal.
  const transactedAmount =
    invoiceAmount !== undefined && invoiceAmount !== null
      ? invoiceAmount
      : currentTransactedBookingAmount;
  const exceedsTransacted =
    transactedAmount !== undefined &&
    transactedAmount !== null &&
    hasAgreed &&
    numericAgreed > transactedAmount;

  // Disputed amount = transacted − client-agreed (never negative), mirroring
  // the backend formula in the disputes list mapping.
  const disputedBookingAmount = hasAgreed
    ? Math.max((transactedAmount || 0) - numericAgreed, 0)
    : 0;
  const feeDifferences =
    (transactedPlatformFee || 0) - (adjustedPlatformFee || 0);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    incoming.forEach((file) => {
      if (!isSupportedFile(file)) {
        toast.error(`${file.name}: unsupported file type`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name}: exceeds ${MAX_FILE_SIZE_LABEL} limit`);
        return;
      }
      if (file.size === 0) {
        toast.error(`${file.name}: file is empty`);
        return;
      }
      accepted.push(file);
    });
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  };

  const handleFileInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const canSubmit =
    hasAgreed &&
    numericAgreed >= 0 &&
    !exceedsTransacted &&
    reason !== "" &&
    !(attachmentRequired && files.length === 0) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isAppeal) {
        await appealDispute(disputeId, {
          clientAgreedBookingAmount: numericAgreed,
          disputeReason: reason,
          issueDescription: description.trim(),
          files,
        });
        toast.success("Appeal submitted");
      } else {
        await submitDispute({
          htRoomConfirmationCode: htConfirmationId,
          clientAgreedBookingAmount: numericAgreed,
          disputeReason: reason,
          issueDescription: description.trim(),
          files,
        });
        toast.success("Dispute submitted");
      }
      onSubmitted?.();
      onClose();
    } catch (err) {
      if (err?.message !== "SESSION_EXPIRED") {
        toast.error(err?.message || "Failed to submit. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hasBookingDetails =
    bookingDetails &&
    (bookingDetails.clientConfirmationId ||
      bookingDetails.bookingType ||
      bookingDetails.guestName ||
      bookingDetails.propertyName);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      centered
      backdrop="static"
      contentClassName="dispute-form-modal-content"
    >
      <ModalBody className="dispute-form-modal-body">
        <h2 className="dispute-form-title">
          {isAppeal ? "Appeal Dispute" : "Submit New Dispute"}
        </h2>

        <div className="dispute-form-grid">
          {/* Left column — booking context */}
          <div className="dispute-form-col">
            <label className="dispute-form-section-label">
              HT Confirmation ID
            </label>
            <input
              className="dispute-form-readonly-input"
              value={htConfirmationId || ""}
              readOnly
            />

            {hasBookingDetails && (
              <>
                <label className="dispute-form-section-label">
                  Booking Details
                </label>
                <div className="dispute-form-booking-box">
                  {bookingDetails.propertyName && (
                    <ReadOnlyField
                      label="Property Name:"
                      value={bookingDetails.propertyName}
                    />
                  )}
                  <ReadOnlyField
                    label="Client Confirmation ID:"
                    value={bookingDetails.clientConfirmationId}
                  />
                  {bookingDetails.bookingType && (
                    <ReadOnlyField
                      label="Booking Type"
                      value={bookingDetails.bookingType}
                    />
                  )}
                  {bookingDetails.guestName && (
                    <ReadOnlyField
                      label="Guest Name:"
                      value={bookingDetails.guestName}
                    />
                  )}
                  {bookingDetails.bookingDate && (
                    <ReadOnlyField
                      label="Booking Date:"
                      value={formatDate(bookingDetails.bookingDate)}
                    />
                  )}
                  {bookingDetails.checkInDate && (
                    <ReadOnlyField
                      label="Check-in Date:"
                      value={formatDate(bookingDetails.checkInDate)}
                    />
                  )}
                  {bookingDetails.checkOutDate && (
                    <ReadOnlyField
                      label="Check-out Date:"
                      value={formatDate(bookingDetails.checkOutDate)}
                    />
                  )}
                </div>
              </>
            )}

            <div className="dispute-amount-row">
              <AmountTile
                label="Transacted Booking Amount"
                value={transactedAmount}
                currency={currency}
              />
              <AmountTile
                label="Transacted Platform Fee"
                value={transactedPlatformFee}
                currency={currency}
              />

              <div className="dispute-form-input-tile">
                <label
                  className="dispute-amount-tile-label"
                  htmlFor="agreed-amount"
                >
                  Client Agreed Booking Amount{" "}
                  <span className="dispute-required">*</span>
                </label>
                <div className="dispute-form-currency-input">
                  <span className="dispute-form-currency-symbol">$</span>
                  <input
                    id="agreed-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={agreedAmount}
                    onChange={(e) => setAgreedAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
              </div>
              <AmountTile
                label="Adjusted Platform Fee"
                value={adjustedPlatformFee}
                currency={currency}
              />

              <AmountTile
                label="Disputed Booking Amount"
                value={disputedBookingAmount}
                currency={currency}
                highlight
              />
              <AmountTile
                label="Fee Differences"
                value={feeDifferences}
                currency={currency}
              />
            </div>
            {exceedsTransacted && (
              <span className="dispute-form-error">
                Cannot exceed the current transacted booking amount.
              </span>
            )}
          </div>

          {/* Right column — reason, description, attachments */}
          <div className="dispute-form-col">
            <label
              className="dispute-form-section-label"
              htmlFor="dispute-reason"
            >
              Dispute Reason <span className="dispute-required">*</span>
            </label>
            <select
              id="dispute-reason"
              className="dispute-form-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="" disabled>
                Select a reason
              </option>
              {DISPUTE_REASONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <label
              className="dispute-form-section-label"
              htmlFor="dispute-description"
            >
              Issue Description
            </label>
            <textarea
              id="dispute-description"
              className="dispute-form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide accompanying details to justify your reason"
              rows={5}
              maxLength={4000}
            />

            <label className="dispute-form-section-label">
              Supporting Materials
              {attachmentRequired && (
                <span className="dispute-required"> *</span>
              )}
            </label>
            <div className="dispute-form-upload">
              <div className="dispute-form-upload-icon">⤓</div>
              <div className="dispute-form-upload-text">
                Upload supporting documents
              </div>
              <div className="dispute-form-upload-hint">
                PDF, JPG, PNG, DOCX, XLSX (max {MAX_FILE_SIZE_LABEL} per file)
              </div>
              <button
                type="button"
                className="dispute-form-choose-files"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose Files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SUPPORTED_FILE_ACCEPT}
                onChange={handleFileInputChange}
                style={{ display: "none" }}
              />
            </div>
            {attachmentRequired && files.length === 0 && (
              <span className="dispute-form-error">
                This reason requires at least one supporting document.
              </span>
            )}

            {files.length > 0 && (
              <ul className="dispute-form-file-list">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="dispute-form-file-item"
                  >
                    <span className="dispute-form-file-name">{file.name}</span>
                    <button
                      type="button"
                      className="dispute-form-file-remove"
                      onClick={() => removeFile(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="dispute-form-footer">
          <button
            type="button"
            className="dispute-btn dispute-btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Discard
          </button>
          <button
            type="button"
            className="dispute-btn dispute-btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

DisputeFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["submit", "appeal"]).isRequired,
  htConfirmationId: PropTypes.string,
  bookingDetails: PropTypes.object,
  invoiceAmount: PropTypes.number,
  transactedPlatformFee: PropTypes.number,
  adjustedPlatformFee: PropTypes.number,
  currency: PropTypes.string,
  currentTransactedBookingAmount: PropTypes.number,
  defaultAgreedAmount: PropTypes.number,
  disputeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func,
};

export default DisputeFormModal;
